import {Injectable, Injector} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router, RouterStateSnapshot} from '@angular/router';
import {BehaviorSubject} from 'rxjs';
import {ApiService} from '@abstract/api.service';
import {ErrorLoggerService} from './error-logger.service';
import {EventsService} from './events.service';
import {catchError, map} from 'rxjs/operators';
import {of} from 'rxjs';
import {UserNamespace} from '@namespaces/user.namespace';
import {User} from '@models/user';

@Injectable()
export class AuthService extends ApiService {
    private logoutUrl = 'auth/logout';
    private infoUrl = 'user/current';

    /**
     * Запомненное состояние роута, чтобы редиректнуть на него после авторизации
     */
    private routerStateSnapshot: RouterStateSnapshot = null;

    private isFetching = false;

    public user$ = new BehaviorSubject<User>(null);

    constructor(
        http: HttpClient,
        logger: ErrorLoggerService,
        injector: Injector,
        private router: Router,
        private events: EventsService,
    ) {
        super(http, logger, injector);
    }

    getToken(): string {
        return localStorage.getItem('token');
    }

    isAuthentificated() {
        return Boolean(this.getToken());
    }

    updateUserInfo() {
        return this.get({
            url: this.infoUrl
        }).pipe(
            map((info: UserNamespace.UserApiInterface) => {
                if (info) {
                    this.user$.next(new User(info));
                    /**
                     * Если запрещено авторизоваться, то разлогиниваем
                     */
                    if (!this.hasPermissions([UserNamespace.Permission.login])) {
                        this.logout();
                    }
                }
                return Boolean(info);
            }),
            catchError((err) => {
                this.logout(() => {
                    const message =
                        'Возможно ваш аккаунт заблокирован. Повторите попытку позднее или обратитесь в службу технической поддержки';
                    this.events.notyShow$.next({
                        title: 'Не удалось авторизоваться',
                        content: message,
                        type: 'error',
                        hideOnRouteChange: false
                    });
                });
                return of(false);
            })
        );
    }

    /**
     * Имеет ли пользователь разрешения
     * @param checkArray
     */
    hasPermissions(checkArray: UserNamespace.Permission[] = []): boolean {
        const user = this.user$.getValue();
        return user ? user.hasPermissions(checkArray) : false;
    }

    login(token: string) {
        this.storeSession(token);
    }

    logout(callback?: Function) {
        if (this.isFetching) {
            return;
        }

        this.isFetching = true;
        this.get({
            url: this.logoutUrl
        }).toPromise().then(() => {
            this.isFetching = false;
            this.clearSession();
            if (callback) {
                callback();
            }
        }, (error) => {
            this.isFetching = false;
            this.clearSession();
        });
    }

    setRedirectUrl(state: RouterStateSnapshot) {
        this.routerStateSnapshot = state;
    }

    private storeSession(token: string) {
        localStorage.setItem('token', token);

        this.events.login$.next();

        /**
         * Если нет запомненного состояния роута, то просто редиректим на главную
         */
        if (!this.routerStateSnapshot) {
            this.router.navigate(['/']);
        } else {

            /**
             * Достаем из состояния сохранный там URL без Query параметров
             */
            let url = this.routerStateSnapshot.url;
            if (url.indexOf('?') >= 0) {
                url = url.substring(0, url.indexOf('?'));
            }

            /**
             * Достаем Query параметры, если они есть
             */
            let params = {};
            if (this.router.parseUrl(this.routerStateSnapshot.url).queryParams) {
                params = this.router.parseUrl(this.routerStateSnapshot.url).queryParams;
            }

            /**
             * Переходим по сохранному URL с Query параметрами
             */
            this.router.navigate([url], {queryParams: params});
        }


    }

    clearSession() {
        this.user$.next(null);
        localStorage.removeItem('token');

        /**
         * Запоминаем последний URL, чтобы после повторной авторизации на него вернуться
         * Не запоминаем url на страницу авторизации
         */
        if (this.router.routerState.snapshot.url.indexOf('/login') >= 0) {
            this.setRedirectUrl(null);
        } else {
            this.setRedirectUrl(this.router.routerState.snapshot);
        }

        this.events.logout$.next();

        this.router.navigate(['/login']);
    }
}
